import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { requestOtp, login as authLogin } from '../controllers/authController';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [generatedOtp, setGeneratedOtp] = useState(null);
  const [stage, setStage] = useState('email'); // 'email' | 'verifyy'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);
  const navigate = useNavigate();

  const sendOtp = async () => {
    setError(null);
    setInfo(null);
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address.');
      return;
    }
    setLoading(true);
    try {
      // call the backend to request OTP
      const res = await requestOtp(email);
      // backend may return a friendly message
      setStage('verify');
      setInfo(res.message || `OTP requested for ${email}`);
      // Do not store any OTP on client in production. Keep generatedOtp only for legacy/demo endpoints
      if (res.otp) setGeneratedOtp(String(res.otp));
    } catch (e) {
      setError(e.message || 'Failed to request OTP');
    } finally {
      setLoading(false);
    }
  };

  const verifyOtp = async () => {
    setError(null);
    if (!otp) {
      setError('Enter the OTP.');
      return;
    }
    setLoading(true);
    try {
      const res = await authLogin(email, otp);
      // success response expected to include admin
      if (res && res.admin) {
        try {
          localStorage.setItem('admin', JSON.stringify(res.admin));
        } catch (e) {
          // ignore storage errors
        }
        navigate('/admin/dashboard', { replace: true });
        return;
      }
      setError(res.message || 'Login failed');
    } catch (e) {
      setError(e.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="login-page"
      style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}
    >
      <div style={{ display: 'flex', gap: 28, alignItems: 'center', maxWidth: 980, width: '100%' }}>
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ textAlign: 'left' }}>
            <h1 style={{ margin: 0, fontSize: '1.8rem' }}>Welcome to</h1>
            <h2 style={{ margin: '6px 0 12px', fontSize: '2.2rem', color: 'var(--accent)' }}>Gaming Science</h2>
            <p style={{ margin: 0, color: 'var(--text-secondary)', maxWidth: 420 }}>
              Manage games, users and transactions. Sign in with your Gmail to continue to the admin console.
            </p>
          </div>
        </div>

        <div className="game-form-wrapper" style={{ width: 420, maxWidth: '96%', padding: 0 }}>
          <div className="game-form-header" style={{ padding: '20px 24px' }}>
            <div>
              <h2>Sign in</h2>
              <p className="game-form-subtitle">Sign in with your Gmail — we'll send a one-time code.</p>
            </div>
          </div>

          <div className="game-form-body">
            {error && <div className={`alert alert-error`}>{error}</div>}
            {info && <div className={`alert alert-success`}>{info}</div>}

            {stage === 'email' && (
              <>
                <div className="game-form-group">
                  <label htmlFor="login-email">Gmail address</label>
                  <input
                    id="login-email"
                    name="login-email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@gmail.com"
                  />
                </div>

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
                  <button
                    type="button"
                    className="button-secondary"
                    onClick={() => {
                      setEmail('');
                      setError(null);
                      setInfo(null);
                    }}
                  >
                    Clear
                  </button>
                  <button type="button" className="button-primary" onClick={async () => {
                    // If backend requires adminId, prompt user to enter it
                    try {
                      await sendOtp();
                    } catch (err) {
                      // If backend response says adminId required, show helpful message
                      if (err.message && err.message.toLowerCase().includes('admin id')) {
                        setError('Admin ID required — please register first or provide Admin ID to send OTP.');
                      } else {
                        setError(err.message || 'Failed to request OTP');
                      }
                    }
                  }} disabled={loading}>
                    {loading ? 'Sending...' : 'Send OTP'}
                  </button>
                </div>
              </>
            )}

            {stage === 'verify' && (
              <>
                <div className="game-form-group">
                  <label htmlFor="login-otp">One-time code</label>
                  <input
                    id="login-otp"
                    name="login-otp"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value)}
                    placeholder="Enter 6-digit code"
                  />
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                  <button
                    type="button"
                    className="button-secondary"
                    onClick={() => {
                      setStage('email');
                      setGeneratedOtp(null);
                      setOtp('');
                      setInfo(null);
                    }}
                  >
                    Change Email
                  </button>

                  <div style={{ display: 'flex', gap: 12 }}>
                    <button type="button" className="button-secondary" onClick={sendOtp}>
                      Resend
                    </button>
                    <button type="button" className="button-primary" onClick={verifyOtp}>
                      Verify & Sign in
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}


